import type { ReactNode } from "react";
import { STEP_LABELS, WIZARD_STEPS, type WizardStep } from "./itemDraft";

/**
 * G-90 — the frame the six create-item screens sit in: progress rail, heading,
 * one step body, and a bottom action bar under the thumb.
 *
 * Deliberately dumb. It knows about steps and nothing about items, so the rules
 * live in `itemDraft.ts` and the questions live in `ItemCreatePage.tsx`.
 *
 * It is a new component rather than a reuse: `OrderBuildPage`'s `.pob-progress`
 * rail is a three-column grid with the step names written into the markup, and
 * six named segments do not fit a phone. The bar itself follows the one bottom
 * bar the app already has, `.pob-cart-bar` — fixed on phones, sticky on wider
 * screens, clear of the safe-area inset.
 */
interface WizardShellProps {
  /** The screen being shown. "done" hides the rail — you cannot go back to it. */
  current: WizardStep | "done";
  /** How far the user has reached; later segments are not yet reachable. */
  furthestIndex: number;
  onJump: (step: WizardStep) => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** The bottom bar's contents. Absent on the done screen, which has its own. */
  actions?: ReactNode;
}

export function WizardShell({
  current,
  furthestIndex,
  onJump,
  title,
  subtitle,
  children,
  actions,
}: WizardShellProps) {
  const currentIndex = WIZARD_STEPS.indexOf(current as WizardStep);
  const onDoneScreen = current === "done";

  return (
    <section className="iw-page">
      {onDoneScreen ? null : (
        <nav className="iw-rail-wrap" aria-label="Progress">
          <ol className="iw-rail">
            {WIZARD_STEPS.map((step, index) => {
              const state =
                index === currentIndex ? "active" : index < currentIndex ? "complete" : "todo";
              return (
                <li key={step}>
                  <button
                    type="button"
                    className="iw-rail-seg"
                    data-state={state}
                    disabled={index > furthestIndex}
                    aria-current={index === currentIndex ? "step" : undefined}
                    aria-label={`Step ${index + 1} of ${WIZARD_STEPS.length}: ${STEP_LABELS[step]}`}
                    onClick={() => onJump(step)}
                  />
                </li>
              );
            })}
          </ol>
          <p className="iw-rail-label">
            <span>{STEP_LABELS[WIZARD_STEPS[currentIndex]]}</span>
            <b>
              Step {currentIndex + 1} of {WIZARD_STEPS.length}
            </b>
          </p>
        </nav>
      )}

      <header className="iw-head">
        <h2 className="page-title">{title}</h2>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </header>

      {/* Keyed by screen so each one mounts fresh and its entry animation runs. */}
      <div className="iw-step" key={current}>
        {children}
      </div>

      {actions ? <div className="iw-bar">{actions}</div> : null}
    </section>
  );
}
